import React, { useState, useEffect, useCallback } from 'react';
import { purchasesVendorsApi, type VendorDto } from '../../services/api';
import AddVendorModal, { type VendorFormData } from './AddVendorModal';

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function VendorsPage() {
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<VendorDto | null>(null);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchasesVendorsApi.list(search || undefined);
      setVendors(res.data ?? []);
    } catch {
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleSubmit = async (data: VendorFormData) => {
    const payload = {
      vendor_name: data.vendor_name.trim(),
      display_name: data.display_name.trim() || undefined,
      email: data.email.trim() || undefined,
      phone: data.phone.trim() || undefined,
      tax_id: data.tax_id.trim() || undefined,
      street_address: data.street_address.trim() || undefined,
      city: data.city.trim() || undefined,
      state_province: data.state_province.trim() || undefined,
      postal_code: data.postal_code.trim() || undefined,
      country: data.country.trim() || undefined,
      payment_terms: data.payment_terms.trim() || undefined,
      status: data.status || 'Active',
    };
    if (editVendor) {
      const res = await purchasesVendorsApi.update(editVendor.id, payload);
      if (res.errors) throw res;
    } else {
      const res = await purchasesVendorsApi.create(payload);
      if (res.errors) throw res;
    }
    await fetchVendors();
  };

  const openAdd = () => {
    setEditVendor(null);
    setModalOpen(true);
  };

  const openEdit = (v: VendorDto) => {
    setEditVendor(v);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this vendor? This cannot be undone.')) return;
    try {
      await purchasesVendorsApi.delete(id);
      await fetchVendors();
    } catch {
      // show toast in real app
    }
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your suppliers and vendors</p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Vendor
        </button>
      </div>

      <div className="mb-4 shrink-0">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-500 text-sm">Loading vendors...</div>
          </div>
        ) : vendors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <svg
              className="w-12 h-12 text-gray-300 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 17a2 2 0 11-4 0 2 2 0 014 0zM16 17a2 2 0 11-4 0 2 2 0 014 0zM5.5 17H4.5a2 2 0 01-2-2v-6a2 2 0 012-2h10a2 2 0 012 2v2m-2 4h.01"
              />
            </svg>
            <p className="font-medium text-gray-600">No vendors yet</p>
            <p className="text-sm mt-1">Add your first vendor to get started</p>
            <button
              type="button"
              onClick={openAdd}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
            >
              + Add Vendor
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Vendor Name
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Contact
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Outstanding
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Terms
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                    Status
                  </th>
                  <th className="w-24 py-3 px-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" aria-label="Actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900">{v.vendor_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5 text-sm text-gray-600">
                        {v.email && (
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {v.email}
                          </span>
                        )}
                        {v.phone && (
                          <span className="inline-flex items-center gap-1.5">
                            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {v.phone}
                          </span>
                        )}
                        {!v.email && !v.phone && (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={
                          v.outstanding > 0 ? 'text-red-600 font-medium' : 'text-gray-900'
                        }
                      >
                        {formatPeso(v.outstanding)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">{v.payment_terms || '—'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          v.status === 'Active'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {v.status}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(v)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-600 transition-colors"
                          aria-label="Edit vendor"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(v.id)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                          aria-label="Delete vendor"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddVendorModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditVendor(null);
        }}
        onSubmit={handleSubmit}
        editVendor={editVendor}
      />
    </div>
  );
}
